import type { OrderKind } from "./types/userDashboard"

export const WAREHOUSES = ["any", "riga", "denver"]

export const availableInCountry = ({
  countryCode,
  availability,
  allowedCountries,
}: {
  countryCode: string | undefined
  availability: string[] | undefined
  allowedCountries: string[] | undefined
}): boolean => {
  if (!availability || !availability.length) return true

  if (!allowedCountries || !countryCode) return false

  return (
    allowedCountries.includes(countryCode) && availability.includes(countryCode)
  )
}

type UsDomesticDestinationKind = "territory" | "freelyAssociatedState" | "military"
type UsDomesticCustomsRule = "never" | "always" | "16oz"

type UsDomesticDestinationEntry = {
  kind: UsDomesticDestinationKind
  customsForm: UsDomesticCustomsRule
}

/**
 * USPS territories and freely associated states, and their outbound
 * customs-form rule. These codes are safe to match against either the
 * country code or a US state code: ISO 3166-1 assigns each of them to the
 * same place USPS does.
 *
 * Sources: USPS DMM 608 2.1-2.3.1 (https://pe.usps.com/text/dmm300/608.htm)
 * and the USPS domestic-destinations FAQ
 * (https://faq.usps.com/s/article/What-US-Possessions-US-Territories-and-Freely-Associated-States-are-Considered-Domestic).
 *
 * DMM 608 2.3.1 exempts Priority Mail Express from the customs form; we
 * don't model that because our express label buys the cheapest Priority
 * Mail rate, not Express.
 */
const US_TERRITORY_DESTINATIONS: Record<string, UsDomesticDestinationEntry> = {
  // Territories. PR/VI never need a customs form (DMM 608 2.4.6); GU/MP do above 16oz.
  PR: { kind: "territory", customsForm: "never" },
  VI: { kind: "territory", customsForm: "never" },
  GU: { kind: "territory", customsForm: "16oz" },
  AS: { kind: "territory", customsForm: "16oz" },
  MP: { kind: "territory", customsForm: "16oz" },
  // Freely Associated States: mail is "treated as domestic" per the USPS FAQ.
  FM: { kind: "freelyAssociatedState", customsForm: "16oz" },
  MH: { kind: "freelyAssociatedState", customsForm: "16oz" },
  PW: { kind: "freelyAssociatedState", customsForm: "16oz" },
}

/**
 * USPS military mail (APO/FPO/DPO), state-coded AA/AE/AP. We always attach
 * a customs form here per DMM 703.2.3.7, regardless of weight.
 *
 * Kept separate from US_TERRITORY_DESTINATIONS: unlike territory codes,
 * AA/AE/AP are not assigned to these places in ISO 3166-1 — AE is the
 * United Arab Emirates there — so these codes must only ever be matched
 * against a US state code, never against a country code.
 */
const US_MILITARY_STATE_CODES: Record<string, UsDomesticDestinationEntry> = {
  AA: { kind: "military", customsForm: "always" },
  AE: { kind: "military", customsForm: "always" },
  AP: { kind: "military", customsForm: "always" },
}

export const US_DOMESTIC_CUSTOMS_WEIGHT_THRESHOLD_OUNCES = 16

/**
 * Whether a country code is a USPS domestic destination: the US itself, or
 * any territory/freely-associated-state code that can appear as the
 * country code on an address. Military mail is always `countryCode: "US"`,
 * so it's already covered by the `US` check.
 */
export const isUsDomesticDestination = (countryCode: string): boolean => {
  const code = countryCode.toUpperCase()
  return code === "US" || code in US_TERRITORY_DESTINATIONS
}

// USPS military addresses always carry state AA/AE/AP, and US addresses are
// validated to a resolvable state code before reaching here
// (see getShippingStateCode in states.ts), so the state alone identifies
// military mail — no need to also parse the APO/FPO/DPO city name.
const resolveUsDomesticDestination = (dest: {
  countryCode: string
  stateCode?: string | null
}) => {
  const countryCode = dest.countryCode.toUpperCase()
  const byCountry = US_TERRITORY_DESTINATIONS[countryCode]
  if (byCountry) return byCountry

  if (countryCode !== "US") return null

  const stateCode = dest.stateCode?.toUpperCase()
  if (!stateCode) return null

  return US_TERRITORY_DESTINATIONS[stateCode] ?? US_MILITARY_STATE_CODES[stateCode] ?? null
}

/**
 * Whether a destination is a US territory, freely associated state, or
 * military (APO/FPO/DPO) address — the set that needs carrier/return-address
 * handling different from the 50 states + DC. Assumes state names have
 * already been normalized to codes (see `getShippingStateCode` in
 * states.ts); this only resolves codes, it doesn't parse names.
 */
/**
 * Whether a code names a US territory (PR, VI, GU, AS, MP). Per the table
 * above these codes are interchangeable between the country and US-state
 * slots of an address, so callers that key off a jurisdiction have to accept
 * both placements.
 *
 * Freely associated states are excluded on purpose: USPS carries their mail
 * as domestic, but FM/MH/PW are sovereign nations, not US jurisdictions.
 */
export const isUsTerritoryCode = (code?: string | null): boolean =>
  !!code && US_TERRITORY_DESTINATIONS[code.toUpperCase()]?.kind === "territory"

export const isUsTerritoryOrMilitaryDestination = (dest: {
  countryCode: string
  stateCode?: string | null
}): boolean => resolveUsDomesticDestination(dest) !== null

/**
 * Whether an outbound label needs a customs declaration, per DMM 608 2.3.1:
 * never for PR/VI, always for military, and only at/above 16oz for the
 * other territories and freely associated states.
 */
export const requiresUsDomesticCustomsForm = (dest: {
  countryCode: string
  stateCode?: string | null
  weightOunces: number
}): boolean => {
  const entry = resolveUsDomesticDestination(dest)
  if (!entry) return false
  if (entry.customsForm === "always") return true
  if (entry.customsForm === "never") return false
  return dest.weightOunces >= US_DOMESTIC_CUSTOMS_WEIGHT_THRESHOLD_OUNCES
}

export const checkIsPrioritySampleOrder = (userProductOrder: {
  kind: OrderKind
  lines: {
    userProduct: {
      productSettings: {
        sku: string
      }
    }
    count: number
  }[]
}): boolean => {
  if (userProductOrder.kind !== "SAMPLE") return false

  const skuCounts = userProductOrder.lines.reduce(
    (acc, line) => {
      acc[line.userProduct.productSettings.sku] =
        (acc[line.userProduct.productSettings.sku] || 0) + line.count
      return acc
    },
    {} as Record<string, number>,
  )

  const hasHighSkuCount = Object.values(skuCounts).some((count) => count > 3)

  return !hasHighSkuCount
}
